import { DataEndpoint } from './remote/DataEndpoint.js';

// --------------------------------------------------------------------------------------------

class CacheSlot {

    constructor() {

        this.name = undefined;
        this.data = undefined; // keep this undefined - used in cached slot to recover from sessionStorage
        this.expires = 0;
        this.expiresIn = 300;
        this.loadPromise = null;

    }

    isExpired() {
        return this.expires < (new Date()).getTime();
    }

    getData() {
        return this.data;
    }

    setData(data) {
        this.data = data;
        this.expires = (new Date()).getTime() + this.expiresIn * 1000;
    }

    async loadData(endpointPath, authManager, forceReload = false) {

        if (this.loadPromise)
            return this.loadPromise;

        if (!forceReload && !this.isExpired())
            return this.getData();

        this.loadPromise = (async () => {
            try {
                const endpoint = new DataEndpoint(endpointPath, authManager);
                const data = await endpoint.queryData();

                this.setData(data);
                this.loadPromise = null;
                return data;

            } catch (error) {
                this.loadPromise = null;
                throw error;
            }
        })();

        return this.loadPromise;

    }

};

// --------------------------------------------------------------------------------------------

class PersistentCacheSlot extends CacheSlot {

    isExpired() {

        return false;

    };

    getData() {

        if (this.data === undefined) {
            try {
                let json = sessionStorage.getItem(`cache.${this.name}`);
                if (json)
                    this.data = JSON.parse(json);
                else
                    this.data = null;
            } catch {
                this.data = null;
            }
        }
        return this.data;


    };

    setData(data) {

        super.setData.call(this, data);

        let json = (data === undefined || data === null) ? null : JSON.stringify(data);

        sessionStorage.setItem(`cache.${this.name}`, json);

    };

}

// --------------------------------------------------------------------------------------------

export class Cache {

    constructor(expirationInterval) {

        this.slots = {};
        this.expirationInterval = expirationInterval || 300;

    };

    createSlot(name, persistent = false, expiresIn = 300) {

        let slot = persistent ? new PersistentCacheSlot() : new CacheSlot();
        slot.name = name;
        slot.expiresIn = expiresIn;

        this.slots[name] = slot;

        return slot;

    };

    getSlot(name, failIfMissing = false) {

        if (!this.slots)
            this.slots = {};

        var slot = this.slots[name];
        if (!slot && failIfMissing)
            throw new Error(`Slot "${name}" not found.`);

        return slot;

    };

    getOrCreateSlot(name, persistent = false, expiresIn = 300) {

        let slot = this.getSlot(name);
        if (!slot)
            slot = this.createSlot(name, persistent, expiresIn);
        return slot;

    }

    isExpired(slotName) {

        return this.getSlot(slotName).isExpired();

    };

    getData(slotName) {

        const slot = this.getSlot(slotName);
        if (!slot)
            return null;
        return slot.getData();

    };

    setData(slotName, data) {

        this.getSlot(slotName).setData(data);

    };

    async loadData(slotName, endpointUrl, authManager, forceReload = false) {

        return this.getSlot(slotName, true).loadData(endpointUrl, authManager, forceReload);

    };

}